/**
 * Site-wide footer: the catalog, the legal documents, a support contact, the
 * community Discord and the operator's name. A payment provider reviewing the
 * site expects these one click away from every page, including the signed-out
 * sign-in screen — so the footer is rendered by every App shell, not by
 * individual pages. It also carries the site-wide "created with AI" notice
 * that stands in for per-card AI chips.
 *
 * Laid out as the brand + copyright beside three labelled columns (service,
 * documents, contact — the support email and the Discord invite, each shown
 * only when configured). Seller details that are not configured yet are left out
 * here rather than printed as "[to be completed]" markers — the legal pages keep
 * those markers, which is where the owner has to see and fill the gap.
 */
import type { MouseEvent } from "react";
import { useT } from "../lib/i18n/store.js";
import { followInApp } from "../lib/links.js";
import { buildPath } from "../lib/route.js";
import { siteInfo, type SiteInfo } from "../lib/siteInfo.js";
import { analyticsConfigured, openCookieSettings } from "../lib/analytics.js";
import { discordInviteUrl } from "../lib/communityLinks.js";
import type { Page } from "../types.js";

type FooterLinkKey = "footer.catalog" | "footer.pricing" | "footer.terms" | "footer.privacy" | "footer.refund";

// The catalog is a real link on every page — the one internal link crawlers
// can follow to the stories from anywhere on the site.
const SERVICE_LINKS: { page: Page; key: FooterLinkKey }[] = [
  { page: "catalog", key: "footer.catalog" },
  { page: "pricing", key: "footer.pricing" }
];
const LEGAL_LINKS: { page: Page; key: FooterLinkKey }[] = [
  { page: "terms", key: "footer.terms" },
  { page: "privacy", key: "footer.privacy" },
  { page: "refund", key: "footer.refund" }
];

/** A configured value, or null while it is still a "[to be completed]" marker. */
function configured(value: string): string | null {
  return value.startsWith("[") ? null : value;
}

export function SiteFooter({
  onNavigate,
  site = siteInfo,
  now = new Date()
}: {
  onNavigate: (page: Page) => void;
  site?: SiteInfo;
  now?: Date;
}) {
  const t = useT();

  // Real hrefs (shareable, crawlable, open-in-new-tab) that route in-app on a
  // plain click.
  function follow(event: MouseEvent<HTMLAnchorElement>, page: Page): void {
    followInApp(event, () => onNavigate(page));
  }

  const link = ({ page, key }: { page: Page; key: FooterLinkKey }) => (
    <li key={page}>
      <a href={buildPath(page, null)} onClick={(event) => follow(event, page)}>
        {t(key)}
      </a>
    </li>
  );

  const email = configured(site.supportEmail);
  const supportEmail = email && email.includes("@") ? email : null;
  const entity = configured(site.entityName) ?? "Lorvok";
  const discordUrl = discordInviteUrl();

  return (
    <footer className="site-footer" aria-label={t("footer.label")}>
      <div className="site-footer-inner">
        <div className="site-footer-brand">
          <span className="site-footer-logo">Lorvok</span>
          <p className="site-footer-meta">{t("footer.copyright", { year: now.getFullYear(), entity })}</p>
          <p className="site-footer-meta">{t("footer.age")}</p>
          <p className="site-footer-meta">{t("ai.siteNotice")}</p>
        </div>

        <nav className="site-footer-col" aria-labelledby="footer-service">
          <h2 id="footer-service">{t("footer.groupService")}</h2>
          <ul>
            {SERVICE_LINKS.map(link)}
            {analyticsConfigured && (
              <li>
                <button type="button" className="site-footer-cookies" onClick={openCookieSettings}>
                  {t("cookies.settings")}
                </button>
              </li>
            )}
          </ul>
        </nav>

        <nav className="site-footer-col" aria-labelledby="footer-legal">
          <h2 id="footer-legal">{t("footer.groupLegal")}</h2>
          <ul>{LEGAL_LINKS.map(link)}</ul>
        </nav>

        {(supportEmail || discordUrl) && (
          <div className="site-footer-col">
            <h2>{t("footer.contact")}</h2>
            <ul>
              {supportEmail && (
                <li>
                  <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                </li>
              )}
              {discordUrl && (
                <li>
                  <a href={discordUrl} target="_blank" rel="noreferrer noopener">
                    {t("footer.discord")}
                  </a>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </footer>
  );
}
